import output from "_src/engine/output";
import loader from "_src/loader";
import { colorUtils, idGenerator } from "_src/utils";
import {propertyType, objectType, photoState, photoOrientation, scanMode} from "_src/constants";
import {createProperty, reformValue, getHexPaletteUnique, deepCopy, getCadType} from "_src/reformer/property";
import { vec3 } from "_src/libs/gl-matrix";
import appState from "_src/app_state";
import {initGeometries, initTextures} from "_src/reformer/common/reformer";

export default {
  async combineObjectStructure(objectProperty, projectPath) {
    switch (objectProperty.getValue("type")) {
      case objectType.SCAN:
      case objectType.POINTCLOUD: {
        const needSurfels = appState.getStateValue("scanMode") !== scanMode.TRIANGLES && Boolean(objectProperty.getValue("surfelsfile"));
        const sourcePath = needSurfels ? objectProperty.getValue("surfelsfile")["jsonref"] : objectProperty.getValue("framesfile")["jsonref"];
        const json = await loader.load(projectPath + sourcePath, loader.JSON, null);
        combineScanProperty(objectProperty, json, needSurfels);

        break;
      }
      case objectType.FUSION: {
        const sourcePath = objectProperty.getValue("geometriesfile")["jsonref"];
        const json = await loader.load(projectPath + sourcePath, loader.JSON, null);
        combineFusionProperty(objectProperty, json);

        break;
      }
      case objectType.CAD: {
        const framesPath = objectProperty.getValue("framesfile")["jsonref"];
        const framesJson = await loader.load(projectPath + framesPath, loader.JSON, null);
        combineCadProperty(objectProperty, framesJson);

        break;
      }
      case objectType.PHOTO_GROUP: {
        const sourcePath = objectProperty.getValue("photosSource")["jsonref"];
        const json = await loader.load(projectPath + sourcePath, loader.JSON, null);
        combinePhotoGroupProperty(objectProperty, json);

        break;
      }
      case objectType.SECTION:
      case objectType.LINEAR_MEASURE:
      case objectType.GEODESIC_MEASURE:
      case objectType.DISTANCE_MAP: {
        const sourcePath = objectProperty.getValue("geometriesSource")["jsonref"];
        const json = await loader.load(projectPath + sourcePath, loader.JSON, null);
        combineMeasureStructure(objectProperty, json);

        break;
      }
    }
  },

  initMeasureProperty(measureData, type) {

    const property = createProperty(propertyType.MEASURE);

    property.insertValue("name", reformValue(measureData, "name", () => ""));
    property.insertValue("id", reformValue(measureData, "id", () => idGenerator.getNext()));
    property.insertValue("uuid", reformValue(measureData["extra"], "uuid", () => ""));
    property.insertValue("visible", reformValue(measureData["extra"], "visible", () => false));
    property.insertValue("measuredObjects", measureData["measured-objects"]);
    property.getValue("properties")["value"] = measureData["total-length"];

    property.insertValue("type", type);
    property.insertValue("geometriesSource", type === objectType.LINEAR_MEASURE ? measureData["linear-points"] : measureData["geodesic-lines"]);

    return property;
  },

  initSectionProperty(sectionData) {

    const property = createProperty(propertyType.MEASURE);

    property.insertValue("name", reformValue(sectionData, "name", () => ""));
    property.insertValue("id", reformValue(sectionData, "id", () => idGenerator.getNext()));
    property.insertValue("uuid", reformValue(sectionData["extra"], "uuid", () => ""));
    property.insertValue("visible", reformValue(sectionData["extra"], "visible", () => false));
    property.insertValue("measuredObjects", sectionData["measured-objects"]);
    property.insertValue("type", objectType.SECTION);

    const propertiesObject = property.getValue("properties");
    propertiesObject["planes"] = sectionData["numcuts"];
    propertiesObject["distance"] = sectionData["stepdelta"];
    propertiesObject["direction"] = sectionData["direction"];

    property.insertValue("geometriesSource", sectionData["zcuts"]);

    property.insertValue("geometries", []);
    const sections = property.getValue("geometries");
    for (const zCutData of sectionData["cuts-visibility"] || sectionData["cuts-info"] || []) {
      for (const sectionData of zCutData["sections"] || []) {
        const section = {
          objectUuid: null,
          transform: null,
          visible: sectionData["visible"],
          selected: false,
          contours: [],
          properties: {
            volume: sectionData["volume"],
            surfaceArea: sectionData["area"],
          },
        };

        for (const contourData of sectionData["contours"] || []) {
          const contour = {
            visible: contourData["visible"],
            selected: false,
            file: null,
            index: null,
            properties: {
              area: contourData["area"],
              perimeter: contourData["perimeter"],
            },
          };
          section.contours.push(contour);
        }
        sections.push(section);
      }
    }

    return property;
  },

  initDistanceMapProperty(distanceMapData) {

    const property = createProperty(propertyType.MEASURE);

    property.insertValue("name", reformValue(distanceMapData, "name", () => ""));
    property.insertValue("id", reformValue(distanceMapData, "id", () => idGenerator.getNext()));
    property.insertValue("uuid", reformValue(distanceMapData["extra"], "uuid", () => ""));
    property.insertValue("type", objectType.DISTANCE_MAP);
    property.insertValue("visible", reformValue(distanceMapData["extra"], "visible", () => false));
    property.insertValue("measuredObjects", distanceMapData["measured-objects"]);
    property.insertValue("searchDistance", distanceMapData["search-distance"]);
    property.insertValue("properties", distanceMapData["statistic"]);
    property.insertValue("geometriesSource", distanceMapData["distances"]);

    return property;
  },

  initObjectProperty(objectData, type, colorList) {

    const property = createProperty(propertyType.OBJECT);

    property.insertValue("name", reformValue(objectData, "name", () => ""));
    property.insertValue("id", reformValue(objectData, "id", () => idGenerator.getNext()));
    property.insertValue("visible", reformValue(objectData["extra"], "visible", () => true));
    property.insertValue("locked", reformValue(objectData["extra"], "locked", () => false));
    property.insertValue("color", reformValue(objectData["extra"], "color", () => getHexPaletteUnique(colorList)));
    property.insertValue("uuid", reformValue(objectData["extra"], "uuid", () => ""));
    property.insertValue("groupID", reformValue(objectData, "groupid", () => ""));

    property.insertValue("scannertype", reformValue(objectData["extra"], "scannertype", () => ""));
    property.insertValue("deviceserial", reformValue(objectData["extra"], "deviceserial", () => ""));
    property.insertValue("datasize", reformValue(objectData["extra"], "storagesize-total", () => 0));
    property.insertValue("surfelsSize", reformValue(objectData["extra"], "storagesize-surfels", () => 0));
    property.insertValue("texturedSize", reformValue(objectData["extra"], "storagesize-textured-geometries", () => 0));
    property.insertValue("untexturedSize", reformValue(objectData["extra"], "storagesize-untextured-geometries", () => 0));
    property.insertValue("isHD", reformValue(objectData["extra"], "ishd", () => false));
    property.insertValue("texturegain", reformValue(objectData["extra"], "texturegain", () => 0.0));
    property.insertValue("texturecameratype", reformValue(objectData["extra"], "texturecameratype", () => 0));
    property.insertValue("textureillumination", reformValue(objectData["extra"], "textureillumination", () => 0.0));
    property.insertValue("globalapplied", reformValue(objectData["extra"], "globalapplied", () => false));

    property.insertValue("textures", reformValue(objectData, "textures", () => []));

    property.insertValue("transform", reformValue(objectData, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
    property.insertValue("projection", reformValue(objectData, "projection", () => [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]));
    property.insertValue("correction", reformValue(objectData, "correction", () => [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]));

    property.insertValue("surfelsfile", objectData["surfels"] ? deepCopy(objectData["surfels"]) : null);
    property.insertValue("geometriesfile", objectData["geometries"] ? deepCopy(objectData["geometries"]) : null);
    property.insertValue("framesfile", objectData["frames"] ? deepCopy(objectData["frames"]) : null);

    property.insertValue("type", type);
    property.insertValue("numPolygons", reformValue(objectData["extra"], "triangles", () => 0));
    property.insertValue("numVertices", reformValue(objectData["extra"], "points", () => 0));
    property.insertValue("numSurfels", reformValue(objectData["extra"], "surfelpoints", () => 0));
    property.insertValue("maxError", reformValue(objectData["extra"], "maxregerror", () => 0.0));
    property.insertValue("numFrames", reformValue(objectData["extra"], "numframes", () => 0));
    property.insertValue("numTexturedFrames", reformValue(objectData["extra"], "numtexturedframes", () => 0));

    //hack
    const textureCounter = Math.max(property.getValue("textures").length, property.getValue("numTexturedFrames"));
    property.setValue("numTexturedFrames", textureCounter);

    property.insertValue("textureWidth", 0);
    property.insertValue("textureHeight", 0);

    property.insertValue("cadProperties", reformValue(objectData, "properties", () => new Object()));
    property.insertValue("cadData", objectData["primitive"]);
    property.insertValue("cadModel", objectData["model"]);
    property.insertValue("cadType", getCadType(objectData));

    //cad experimental
    property.insertValue("referencedFusion", objectData["referenced_fusion"]);
    property.insertValue("fitmaskfile", objectData["fit_mask"]);
    property.insertValue("assembly", objectData["assembly"]);

    const hasTargetScan = objectData["related-object-uuid"] !== undefined;
    const hasRawGeometry = objectData["extra"]["hasrawgeometry"] !== undefined ? objectData["extra"]["hasrawgeometry"] : hasTargetScan;
    const targetRawScan = hasTargetScan ? objectData["related-object-uuid"] : objectData["extra"]["uuid"];
    property.insertValue("hasrawgeometry", hasRawGeometry);
    property.insertValue("targetRawScan", targetRawScan);

    return property;
  },

  initPhotoGroupProperty(data) {

    const property = createProperty(propertyType.PHOTO_GROUP);

    property.insertValue("name", reformValue(data, "name", () => ""));
    property.insertValue("id", reformValue(data, "id", () => idGenerator.getNext()));
    property.insertValue("uuid", reformValue(data["extra"], "uuid", () => idGenerator.generateUUID()));
    property.insertValue("groupID", reformValue(data, "groupid", () => ""));
    property.insertValue("visible", reformValue(data["extra"], "visible", () => false));
    property.insertValue("scenePhotoSize", reformValue(data["extra"], "estimated-photo-size", () => 1));
    property.insertValue("projection", reformValue(data, "projection", () => [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]));
    property.insertValue("transform", reformValue(data, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
    property.insertValue("properties", data["extra"]);
    const isRegistered = (data["extra"]["maxregerror"] !== undefined) && (data["extra"]["maxregerror"] >= 0);
    property.insertValue("registered", isRegistered);
    property.insertValue("photosSource", data["frames"] ? deepCopy(data["frames"]) : null);

    const undistMapData = data["undistortion-map"];
    if (undistMapData) {
      const undistMapProperty = createProperty(propertyType.LEVEL);
      undistMapProperty.insertValue("width", reformValue(undistMapData, "width", () => 1));
      undistMapProperty.insertValue("height", reformValue(undistMapData, "height", () => 1));
      undistMapProperty.insertValue("channels", reformValue(undistMapData, "channels", () => 2));
      undistMapProperty.insertValue("index", reformValue(undistMapData, "index", () => 0));
      undistMapProperty.insertValue("file", reformValue(undistMapData, "file", () => ""));
      undistMapProperty.insertValue("hash", undistMapData["hash"] || undistMapData["file"]);

      property.insertValue("undistortionMap", undistMapProperty);
    }

    return property;
  },
}

function combineScanProperty(objectProperty, data, isSurfels) {
  if (isSurfels) {
    const surfelsProperty = createProperty(propertyType.FRAME);
    surfelsProperty.insertValue("name", objectProperty.getValue("name"));
    surfelsProperty.insertValue("id", objectProperty.getValue("id"));
    surfelsProperty.insertValue("color", objectProperty.getValue("color"));

    surfelsProperty.insertValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
    surfelsProperty.insertValue("numSurfels", objectProperty.getValue("numSurfels"));

    surfelsProperty.insertValue("geometries", initGeometries(data));
    objectProperty.getValue("frames").push(surfelsProperty);
  } else {
    for (const frameData of data) {
      const property = createProperty(propertyType.FRAME);

      property.insertValue("name", reformValue(frameData, "name", () => ""));
      property.insertValue("id", reformValue(frameData, "id", () => ""));
      property.insertValue("color", reformValue(frameData["extra"], "color", () => colorUtils.generateHexColor()));
      property.insertValue("error", reformValue(frameData["extra"], "regerror", () => 0.0));
      property.insertValue("keyframe", reformValue(frameData["extra"], "keyframe", () => false));

      property.insertValue("transform", reformValue(frameData, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
      property.insertValue("numPolygons", reformValue(frameData, "numPolygons", () => 0));
      property.insertValue("numVertices", reformValue(frameData, "numVertices", () => 0));

      const textures = frameData["textures"] || frameData["distorted"] || [];
      const geometries = frameData["geometries"] || [];
      const trackings = frameData["trackings"] || [];

      property.insertValue("textured", textures.length > 0);
      property.insertValue("textures", initTextures(textures));
      property.insertValue("geometries", initGeometries(geometries));
      property.insertValue("trackings", deepCopy(trackings));

      objectProperty.getValue("frames").push(property);
    }
  }
}

function combineFusionProperty(objectProperty, geometries) {
  const property = createProperty(propertyType.FRAME);

  property.insertValue("name", objectProperty.getValue("name"));
  property.insertValue("id", objectProperty.getValue("id"));
  property.insertValue("color", objectProperty.getValue("color"));

  property.insertValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  property.insertValue("numPolygons", objectProperty.getValue("numPolygons"));
  property.insertValue("numVertices", objectProperty.getValue("numVertices"));

  const textures = objectProperty.getValue("textures");

  property.insertValue("textures", initTextures(textures));
  property.insertValue("geometries", initGeometries(geometries));
  property.insertValue("textured", textures.length > 0);

  objectProperty.getValue("frames").push(property);
}

function combineCadProperty(objectProperty, framesData) {
  const frames = objectProperty.getValue("frames");
  for (const frameData of framesData) {
    const property = createProperty(propertyType.FRAME);

    property.insertValue("name", reformValue(frameData, "name", () => ""));
    property.insertValue("id", reformValue(frameData, "id", () => ""));
    property.insertValue("color", reformValue(frameData["extra"], "color", () => colorUtils.generateHexColor()));
    property.insertValue("error", reformValue(frameData["extra"], "regerror", () => 0.0));
    property.insertValue("keyframe", reformValue(frameData["extra"], "keyframe", () => false));

    property.insertValue("transform", reformValue(frameData, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
    property.insertValue("numPolygons", reformValue(frameData, "numPolygons", () => 0));
    property.insertValue("numVertices", reformValue(frameData, "numVertices", () => 0));

    const textures = frameData["textures"] || [];
    const geometries = frameData["geometries"] || [];
    const trackings = frameData["trackings"] || [];

    property.insertValue("textured", textures.length > 0);
    property.insertValue("textures", initTextures(textures));
    property.insertValue("geometries", initGeometries(geometries));
    property.insertValue("trackings", deepCopy(trackings));

    frames.push(property);
  }
}

function combinePhotoGroupProperty(property, photos) {
  property.setValue("photos", photos);
  for (const photo of property.getValue("photos")) {
    photo["state"] = photoState.DEFAULT;
    photo["properties"] = photo["extra"];
    const isRegistered = (photo["extra"]["regerror"] !== undefined) && (photo["extra"]["regerror"] >= 0);
    photo["registered"] = isRegistered;
    photo["orientation"] = parsePhotoOrientation(photo["extra"]["orientation"]);
  }
}

function combineMeasureStructure(property, geometries) {
  switch (property.getValue("type")) {
    case objectType.LINEAR_MEASURE: {
      property.setValue("geometries", geometries);
      if (property.getValue("properties")["value"] === undefined) {
        const pointsData = property.getValue("geometries");
        let distance = 0;
        for (let i = 0; i < pointsData.length - 1; i++) {
          distance += vec3.distance(pointsData[i]["position"], pointsData[i + 1]["position"]);
        }
        property.getValue("properties")["value"] = distance;
      }

      break;
    }

    case objectType.GEODESIC_MEASURE: {
      property.setValue("geometries", geometries);
      if (property.getValue("properties")["value"] === undefined) {
        let distance = 0;
        for (const knotData of property.getValue("geometries")) {
          distance += knotData["length"];
        }
        property.getValue("properties")["value"] = distance;
      }

      break;
    }

    case objectType.SECTION: {
      let sectionOffset = 0;
      for (let i=0; i<geometries.length; i++) {
        const zCutData = geometries[i];
        const sectionsData = zCutData["sections"] || [];

        for (let j=0; j<sectionsData.length; j++) {
          const sectionData = sectionsData[j];
          const section = property.getValue("geometries")[sectionOffset + j];
          section.objectUuid = sectionData["object-uuid"];
          section.transform = zCutData["transform"];
          if (section.properties["volume"] === undefined) section.properties["volume"] = sectionData["volume"];
          if (section.properties["surfaceArea"] === undefined) section.properties["surfaceArea"] = sectionData["area"];

          const contoursData = sectionData["contours"] || [];
          for (let k=0; k<contoursData.length; k++) {
            const contourData = contoursData[k];
            const contour = section.contours[k];
            contour.file = contourData["file"];
            contour.index = contourData["index"];
            if (contour.properties["area"] === undefined) contour.properties["area"] = contourData["area"];
            if (contour.properties["perimeter"] === undefined) contour.properties["perimeter"] = contourData["perimeter"];
          }
        }

        sectionOffset += sectionsData.length;
      }

      break;
    }

    case objectType.DISTANCE_MAP: {
      property.setValue("geometries", geometries);
      break;
    }

    default: {
      output.error(output.DEVELOPERS_ERROR, "measure type " + property.getValue("type") + " is not supported by combiner");
    }
  }
}

function parsePhotoOrientation(orientationString) {
  switch (orientationString) {
    case "mirror-horizontal":
      return photoOrientation.MIRROR_HORIZONTAL;
    case "rotation270-mirror-horizontal":
      return photoOrientation.ROTATION270_MIRROR_HORIZONTAL;
    case "rotation90-mirror-horizontal":
      return photoOrientation.ROTATION90_MIRROR_HORIZONTAL;
    case "mirror-vertical":
      return photoOrientation.MIRROR_VERTICAL;
    case "rotation90":
      return photoOrientation.ROTATION90;
    case "rotation180":
      return photoOrientation.ROTATION180;
    case "rotation270":
      return photoOrientation.ROTATION270;
    default:
      return photoOrientation.DEFAULT;
  }
}
